/**
 * MessageContent — Renders message text, auto-detecting code blocks.
 *
 * Detects content as code when:
 *  - It is wrapped in fenced backticks (```...```)
 *  - OR it contains ≥3 lines that strongly look like code (presence of
 *    `{`, `;`, `=`, leading 4-space indentation, `def `, `function `, etc.)
 *
 * Code blocks render in a monospace Discord-style card with a copy button.
 * Otherwise the message is rendered as normal whitespace-pre-wrap text.
 */
import React, { useMemo, useState } from 'react';
import { Copy, CheckCircle2 } from 'lucide-react';

const CODE_HEURISTICS = [
  /\{[\s\S]*\}/,           // braces block
  /;\s*$/m,                 // line ending with ;
  /^\s{2,}[a-zA-Z]/m,       // indented code
  /\b(def|function|class|return|import|const|let|var|if|else|for|while|public|private)\b/,
  /=\s*(\(|\{|\[)/,         // assignment with bracket
  /<[a-zA-Z][^>]*>/,        // HTML/JSX tags
];

function detectFence(text) {
  // Matches ```lang\n...\n``` OR plain ```...```
  const m = text.match(/```(?:([a-zA-Z0-9_+-]+)\n)?([\s\S]+?)```/);
  if (m) return { fenced: true, lang: m[1] || '', code: m[2].trim(), before: text.slice(0, m.index), after: text.slice(m.index + m[0].length) };
  return null;
}

function looksLikeCode(text) {
  if (!text) return false;
  const lines = text.split('\n');
  if (lines.length < 3) return false;
  let hits = 0;
  for (const re of CODE_HEURISTICS) if (re.test(text)) hits += 1;
  return hits >= 2;
}

const CodeBlock = ({ code, lang }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div className="my-1.5 rounded-lg overflow-hidden border border-white/10 bg-black/60 text-left" data-testid="code-block">
      <div className="flex items-center justify-between px-3 py-1 bg-white/5 text-[10px] uppercase tracking-wider text-white/50 font-bold">
        <span>{lang || 'code'}</span>
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/10 text-white/80"
          data-testid="code-copy-btn"
        >
          {copied ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copié' : 'Copier'}
        </button>
      </div>
      <pre className="p-3 text-[12px] font-mono text-emerald-200 whitespace-pre overflow-x-auto leading-relaxed">{code}</pre>
    </div>
  );
};

/**
 * Splits a string into nodes: plain text and code blocks. Supports a single
 * fenced block (most common) and a heuristic fallback for the entire message.
 */
const MessageContent = ({ text, renderInline }) => {
  const nodes = useMemo(() => {
    if (!text) return [{ kind: 'text', value: '' }];
    const fence = detectFence(text);
    if (fence) {
      const out = [];
      if (fence.before) out.push({ kind: 'text', value: fence.before });
      out.push({ kind: 'code', value: fence.code, lang: fence.lang });
      if (fence.after) out.push({ kind: 'text', value: fence.after });
      return out;
    }
    if (looksLikeCode(text)) {
      return [{ kind: 'code', value: text, lang: '' }];
    }
    return [{ kind: 'text', value: text }];
  }, [text]);

  return (
    <>
      {nodes.map((n, i) => {
        if (n.kind === 'code') {
          return <CodeBlock key={i} code={n.value} lang={n.lang} />;
        }
        return (
          <span key={i} className="whitespace-pre-wrap break-words">
            {renderInline ? renderInline(n.value) : n.value}
          </span>
        );
      })}
    </>
  );
};

export default MessageContent;
